import type { GitHubRepository } from "@octopusdeploy/octopus-server-client";
import { useEffect, useState } from "react";
export type RepositoryAction = "Existing" | "Adding" | "Removing";
export interface SelectedGitHubRepository extends GitHubRepository {
    action: RepositoryAction;
}
export interface GitHubRepositoryChangeSummary {
    nothingSelected: boolean;
    hasChanges: boolean;
    adding: number;
    removing: number;
}
export function useGitHubConnectionRepositorySelectorState(existingRepositories: GitHubRepository[] | undefined) {
    // Repositories that are not currently on the connection, but the user has selected as part of this session.
    const [repositoriesToAdd, setRepositoriesToAdd] = useState<GitHubRepository[]>([]);
    // Repositories that are currently on the connection and the user has removed them as part of this session.
    const [repositoriesToRemove, setRepositoriesToRemove] = useState<GitHubRepository[]>([]);
    // Calculated field that stores all of the currently selected repositories. Controls what is shown
    // in the selected list (and what we'll save).
    const [selectedRepositories, setSelectedRepositories] = useState<SelectedGitHubRepository[]>([]);
    const [summary, setSummary] = useState<GitHubRepositoryChangeSummary>({
        nothingSelected: false,
        hasChanges: false,
        adding: 0,
        removing: 0,
    });
    useEffect(() => {
        // Calculate the list of repositories that are currently selected. We want to show these in the order
        // of:
        // - Repositories that we're adding
        // - Repositories that we're removing
        // - Existing repositories (that we're not removing)
        const existingRepositoriesExcludingRemoving = existingRepositories ? existingRepositories.filter((repo) => !repositoriesToRemove.some((r) => r.RepositoryId === repo.RepositoryId)) : [];
        const adding = [...repositoriesToAdd].sort((a, b) => a.RepositoryName.localeCompare(b.RepositoryName)).map<SelectedGitHubRepository>((repo) => ({ ...repo, action: "Adding" }));
        const removing = [...repositoriesToRemove].sort((a, b) => a.RepositoryName.localeCompare(b.RepositoryName)).map<SelectedGitHubRepository>((repo) => ({ ...repo, action: "Removing" }));
        const rest = [...existingRepositoriesExcludingRemoving].sort((a, b) => a.RepositoryName.localeCompare(b.RepositoryName)).map<SelectedGitHubRepository>((repo) => ({ ...repo, action: "Existing" }));
        setSelectedRepositories([...adding, ...removing, ...rest]);
        setSummary({
            nothingSelected: repositoriesToAdd.length === 0 && existingRepositoriesExcludingRemoving.length === 0,
            hasChanges: repositoriesToAdd.length > 0 || repositoriesToRemove.length > 0,
            adding: repositoriesToAdd.length,
            removing: repositoriesToRemove.length,
        });
    }, [repositoriesToAdd, repositoriesToRemove, existingRepositories]);
    function selectRepository(repository: GitHubRepository) {
        // Add the repository to the list. If somehow it was already in the list and we're triggering this, make sure we don't add it twice.
        setRepositoriesToAdd((prev) => [...prev.filter((r) => r.RepositoryId !== repository.RepositoryId), repository]);
    }
    function removeRepository(repository: SelectedGitHubRepository) {
        if (repository.action === "Existing") {
            // If it's an existing repository and they're clicking it, they want to remove it from the connection.
            //
            // Add it to the list of repositories to remove.
            setRepositoriesToRemove((prev) => [...prev, repository]);
        }
        else if (repository.action === "Adding") {
            // If it was in adding mode, it means it's not currently on the connection but they had searched for it
            // and selected it. Clicking it means that they no longer want to add it to the connection.
            //
            // Remove it from the list of repositories to add.
            setRepositoriesToAdd((prev) => prev.filter((r) => r.RepositoryId !== repository.RepositoryId));
        }
        else if (repository.action === "Removing") {
            // If it was in removing mode, it's currently on the connection but the user had selected to remove it. This
            // means they don't want to remove it anymore.
            //
            // Remove it from the list of repositories to remove.
            setRepositoriesToRemove((prev) => prev.filter((r) => r.RepositoryId !== repository.RepositoryId));
        }
    }
    function repositoryIsSelected(repository: GitHubRepository) {
        return selectedRepositories.some((r) => r.RepositoryId === repository.RepositoryId);
    }
    return { selectedRepositories, summary, selectRepository, removeRepository, repositoryIsSelected };
}
